'use client';

import { MouseEvent, useEffect, useState } from 'react';
import { getDelegateAccountLabel } from 'src/utils/account';
import MarginUpdateRow from './MarginUpdateRow';
import useDriftAccountStore, {
	AccountData,
} from 'src/stores/useDriftAccountsStore';
import { convertMarginRatioToLeverage } from '@velocity-exchange/common';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useModal from 'src/components/Modals/hooks/useModal';
import TextButton from 'src/components/primitives/TextButton/TextButton';

// Current subaccount on-chain state
type SubAccountInfo = {
	accountId: number;
	accountName: string;
	isDelegate?: boolean;
	maxLeverage: number;
	marginTradingEnabled: boolean;
	reduceOnly: boolean;
};

// Updates for subaccount
export type SubAccountSettingsUpdateEntry = {
	subAccountId?: number;
	maxLeverage?: number; // Displayed as "Max Leverage"
	marginTradingEnabled?: boolean; // Spot margin enabled
	reduceOnly?: boolean;
};

// All subaccount updates keyed by subaccount id
export type AllSubaccountUpdates = Record<
	number,
	SubAccountSettingsUpdateEntry
>;

const MarginSettings = (props: {
	allSubAccountUpdates: Record<number, SubAccountSettingsUpdateEntry>;
	addToMarginRatioUpdates: (
		subaccountId: number,
		newValue: number | undefined
	) => void;
	addToMarginTradingUpdates: (subaccountId: number, newValue: boolean) => void;
	addToReduceOnlyUpdates: (subaccountId: number, newValue: boolean) => void;
}) => {
	const connected = useWalletIsConnected();
	const subAccounts = useDriftAccountStore((s) => s.accounts) as Record<
		string,
		AccountData
	>;
	const [subAccountInfos, setSubAccountInfos] = useState<SubAccountInfo[]>([]);
	const { push } = useModal();

	const fetchSubAccountInfos = () => {
		setSubAccountInfos(
			Object.values(subAccounts)
				.map((acct) => {
					if (!acct.client || !acct.client?.isSubscribed) return;

					const userAccount = acct.client.getUserAccountOrThrow();

					const customMarginRatio = userAccount.maxMarginRatio;

					const maxLeverage = convertMarginRatioToLeverage(customMarginRatio);

					return {
						accountId: acct.userId,
						accountName: acct.isDelegatedTo
							? getDelegateAccountLabel(acct)
							: acct.name,
						isDelegate: acct.isDelegatedTo ?? false,
						maxLeverage,
						marginTradingEnabled: userAccount.isMarginTradingEnabled,
						// Reduce-only status is no longer surfaced on UserAccount after
						// the velocity migration.
						reduceOnly: false,
					};
				})
				.filter(Boolean) as SubAccountInfo[]
		);
	};

	const setMaxLeverage = (accountId: number, newValue: number | undefined) => {
		props.addToMarginRatioUpdates(accountId, newValue);
	};

	const setMarginEnabled = (accountId: number, newValue: boolean) => {
		props.addToMarginTradingUpdates(accountId, newValue);
	};

	const setReduceOnly = (accountId: number, newValue: boolean) => {
		props.addToReduceOnlyUpdates(accountId, newValue);
	};

	const handleAddNewSubaccount = (event: MouseEvent) => {
		event.preventDefault();
		push({ type: 'new_subaccount', props: {} });
	};

	useEffect(() => {
		if (connected) {
			fetchSubAccountInfos();
		}
	}, [connected, subAccounts]);

	return (
		<div className="flex flex-col gap-y-lg">
			<p className="typo-body-xs text-text-secondary">
				The maximum leverage applies to your subaccount&apos;s total leverage,
				not your position. To open an isolated position, you can create a new
				subaccount instead{' '}
				<TextButton
					size="sm"
					variant="tertiary"
					onClick={handleAddNewSubaccount}
				>
					here
				</TextButton>
				.
			</p>
			<div className="flex flex-col gap-y-lg">
				{subAccountInfos
					.filter((subAcct) => !subAcct.isDelegate)
					.map((subAcct) => {
						const hasMaxLeverageSettingUpdate =
							props.allSubAccountUpdates[subAcct.accountId] &&
							Object.keys(
								props.allSubAccountUpdates[subAcct.accountId]
							).includes('maxLeverage');

						const currentMaxLev = hasMaxLeverageSettingUpdate
							? props.allSubAccountUpdates[subAcct.accountId]?.maxLeverage
							: subAcct.maxLeverage;

						return (
							<MarginUpdateRow
								key={`${subAcct.accountName}_margin`}
								label={subAcct.accountName}
								currentMaxLev={currentMaxLev}
								marginEnabledChecked={subAcct.marginTradingEnabled}
								reduceOnly={subAcct.reduceOnly}
								onChangeEnabled={(newValue: boolean) => {
									setMarginEnabled(subAcct.accountId, newValue);
								}}
								onChangeRatio={(newValue: number | undefined) => {
									const valueToPass =
										// `-1` indicates that the form value is the same as the saved value.
										// See `addToMarginRatioUpdates`.
										newValue === subAcct.maxLeverage ? -1 : newValue;

									setMaxLeverage(subAcct.accountId, valueToPass);
								}}
								onChangeReduceOnly={(newValue: boolean) => {
									setReduceOnly(subAcct.accountId, newValue);
								}}
								disabled={false}
							/>
						);
					})}
				{subAccountInfos.filter((subAcct) => subAcct.isDelegate)?.length >
					0 && (
					<span className="typo-label-sm text-text-default">
						Delegated accounts
					</span>
				)}
				{subAccountInfos
					.filter((subAcct) => subAcct.isDelegate)
					.map((subAcct) => {
						const hasMaxLeverageSettingUpdate =
							props.allSubAccountUpdates[subAcct.accountId] &&
							Object.keys(
								props.allSubAccountUpdates[subAcct.accountId]
							).includes('maxLeverage');

						const currentMaxLev = hasMaxLeverageSettingUpdate
							? props.allSubAccountUpdates[subAcct.accountId]?.maxLeverage
							: subAcct.maxLeverage;

						return (
							<MarginUpdateRow
								key={`${subAcct.accountName}_margin`}
								label={subAcct.accountName}
								currentMaxLev={currentMaxLev}
								marginEnabledChecked={subAcct.marginTradingEnabled}
								reduceOnly={subAcct.reduceOnly}
								onChangeEnabled={(_newValue: boolean) => {
									// disabled
								}}
								onChangeRatio={(_newValue: number | undefined) => {
									// disabled
								}}
								onChangeReduceOnly={(_newValue: boolean) => {
									// disabled
								}}
								disabled={true}
							/>
						);
					})}
			</div>
		</div>
	);
};

export default MarginSettings;
